import { Link } from 'react-router-dom';

const DEFAULT_BANNER_COLOR = '#C0392B';

export interface UrgentBannerSettings {
  enabled: boolean;
  label: string;
  message: string;
  ctaLabel: string;
  ctaHref: string;
  backgroundColor: string;
  updatedAt?: unknown;
  updatedBy?: string;
}

interface UrgentBannerProps {
  config: UrgentBannerSettings | null;
}

function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href.trim());
}

function normalizeHexColor(input: string | undefined): string {
  if (!input) return DEFAULT_BANNER_COLOR;
  const value = input.trim();
  const fullHex = /^#([0-9a-f]{6})$/i;
  const shortHex = /^#([0-9a-f]{3})$/i;
  if (fullHex.test(value) || shortHex.test(value)) return value;
  return DEFAULT_BANNER_COLOR;
}

export function UrgentBanner({ config }: Readonly<UrgentBannerProps>): JSX.Element | null {
  if (!config?.enabled) return null;

  const message = config.message?.trim() ?? '';
  if (!message) return null;

  const label = config.label?.trim() || 'URGENT:';
  const ctaLabel = config.ctaLabel?.trim() ?? '';
  const ctaHref = config.ctaHref?.trim() ?? '';
  const bgColor = normalizeHexColor(config.backgroundColor);
  const shouldRenderCta = Boolean(ctaLabel && ctaHref);
  const ctaIsExternal = shouldRenderCta ? isExternalHref(ctaHref) : false;
  let ctaNode: JSX.Element | null = null;
  if (shouldRenderCta && ctaIsExternal) {
    ctaNode = (
      <a
        href={ctaHref}
        target="_blank"
        rel="noopener noreferrer"
        className="font-semibold underline underline-offset-2 hover:opacity-90"
        aria-label={`${ctaLabel} (opens in a new tab)`}
      >
        {ctaLabel}
      </a>
    );
  } else if (shouldRenderCta) {
    ctaNode = (
      <Link
        to={ctaHref}
        className="font-semibold underline underline-offset-2 hover:opacity-90"
        aria-label={ctaLabel}
      >
        {ctaLabel}
      </Link>
    );
  }

  return (
    <section aria-label="Urgent notice" className="relative z-30 border-b border-black/10" style={{ backgroundColor: bgColor }}>
      <div className="mx-auto w-full max-w-7xl px-3 py-2 sm:px-4">
        <p className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center text-xs sm:text-sm text-white">
          <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-full bg-white/95 align-middle" />
          <strong className="font-bold">{label}</strong>
          <span>{message}</span>
          {ctaNode}
        </p>
      </div>
    </section>
  );
}
